import {
  PLANNING_REDONE_MESSAGE,
  PLANNING_UNDONE_MESSAGE,
} from 'constants/messageConstants';
import { PLANNING_UNDO_NOTE_DURATION_MS } from 'constants/uiConstants';
import type { IInput } from 'interfaces/IInput';
import type { IInputTask } from 'interfaces/IInputTask';
import { useCallback, useEffect, useRef, useState } from 'react';

import { describeDraftChange, diffDraftTasks } from './planDraftHistory';

/** Where the note sits, in px from the viewport's top left. */
export interface UndoNotePosition {
  top: number;
  left: number;
}

export interface PlanningUndoNote {
  text: string;
  /** Beside the element that had focus, or null for the top of the tasks area. */
  position: UndoNotePosition | null;
}

// Room the note needs to the right of the focused element before it moves
// below it instead (PlanningUndoNote's max-w-[18rem], at the default 16px
// root size), and the gap between them.
const NOTE_ROOM_PX = 288;
const NOTE_GAP_PX = 8;

/**
 * The note's position beside a focused element: to its right, or below it
 * when there isn't room on the right.
 */
export const placeUndoNote = (
  rect: Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left'>,
  viewportWidth: number
): UndoNotePosition =>
  rect.right + NOTE_GAP_PX + NOTE_ROOM_PX <= viewportWidth
    ? { top: rect.top, left: rect.right + NOTE_GAP_PX }
    : {
        top: rect.bottom + NOTE_GAP_PX / 2,
        left: Math.max(
          NOTE_GAP_PX,
          Math.min(rect.left, viewportWidth - NOTE_ROOM_PX)
        ),
      };

// Typing keeps its own undo; a dialog or popover holds its own edits.
const OWNS_UNDO_SELECTOR =
  'input, textarea, select, [contenteditable="true"], .ant-modal, .ant-popover';

const focusedElementPosition = (): UndoNotePosition | null => {
  const focused = document.activeElement;
  if (!(focused instanceof HTMLElement) || focused === document.body) {
    return null;
  }
  const rect = focused.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  return placeUndoNote(rect, window.innerWidth);
};

interface UsePlanningUndoArgs {
  /** True while Planning shows an open plan the user can edit. */
  enabled: boolean;
  stepDraftHistory: (direction: 'undo' | 'redo') => {
    from: IInput;
    to: IInput;
    step: { before: IInput; after: IInput };
  } | null;
  schedulePendingTaskSave: (task: IInputTask) => void;
  schedulePendingTaskDelete: (taskId: string) => void;
}

/**
 * Keyboard undo in Planning (OP-2539): Ctrl/⌘+Z undoes the last change made
 * to the open plan in this session on this device — never anything changed
 * elsewhere — and Ctrl/⌘+Shift+Z redoes it — except while typing in a
 * field, where the keys undo the typing. The plan draft steps back and the
 * normal autosave sends it; tasks that changed are saved (a deleted task
 * comes back with its products) and tasks that didn't exist are deleted.
 * A short note beside the user's focus says what changed.
 */
export const usePlanningUndo = ({
  enabled,
  stepDraftHistory,
  schedulePendingTaskSave,
  schedulePendingTaskDelete,
}: UsePlanningUndoArgs) => {
  const [note, setNote] = useState<PlanningUndoNote | null>(null);
  const noteTimerRef = useRef<number>();

  const showNote = useCallback((nextNote: PlanningUndoNote) => {
    window.clearTimeout(noteTimerRef.current);
    setNote(nextNote);
    noteTimerRef.current = window.setTimeout(
      () => setNote(null),
      PLANNING_UNDO_NOTE_DURATION_MS
    );
  }, []);
  useEffect(() => () => window.clearTimeout(noteTimerRef.current), []);

  const step = useCallback(
    (direction: 'undo' | 'redo') => {
      // Measured before the change, which may re-render the focused row.
      const position = focusedElementPosition();
      const result = stepDraftHistory(direction);
      if (!result) return false;
      const { upserts, deletes } = diffDraftTasks(
        result.from.tasks ?? [],
        result.to.tasks ?? []
      );
      upserts.forEach(schedulePendingTaskSave);
      deletes.forEach(schedulePendingTaskDelete);
      const change = describeDraftChange(result.step.after, result.step.before);
      const text =
        direction === 'undo'
          ? PLANNING_UNDONE_MESSAGE(change)
          : PLANNING_REDONE_MESSAGE(change);
      showNote({ text, position });
      return true;
    },
    [
      schedulePendingTaskDelete,
      schedulePendingTaskSave,
      showNote,
      stepDraftHistory,
    ]
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() !== 'z' ||
        !(event.metaKey || event.ctrlKey) ||
        event.altKey ||
        event.defaultPrevented
      ) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target?.closest?.(OWNS_UNDO_SELECTOR)) return;
      // Nothing to undo leaves the browser's own behaviour alone.
      if (step(event.shiftKey ? 'redo' : 'undo')) event.preventDefault();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled, step]);

  // A note about another plan, or a hidden Planning tab, is stale.
  useEffect(() => {
    if (!enabled) setNote(null);
  }, [enabled]);

  return { note };
};

export default usePlanningUndo;
